/**
 * PDV — ponto de venda.
 *
 * A tela é o balcão inteiro: catálogo e saldo por SKU à esquerda, cliente,
 * carrinho, desconto, pagamento e fechamento à direita. Quatro coisas do
 * handoff (`Lumma Fit PDV.dc.html`) continuam NÃO existindo e a tela não finge
 * nenhuma delas: turno de caixa, TEF, cobrança PIX e emissão de NFC-e. O que
 * existe de verdade é a venda — `POST /vendas` grava venda, itens, pagamentos,
 * a baixa de estoque e as parcelas do crediário numa transação só.
 *
 * O carrinho é client-side, mas nada aqui grava saldo: quem grava é o gatilho
 * do banco a partir de `movimento_estoque` (INV-04/INV-05), e o saldo mostrado
 * no seletor de variação é sempre o que a API respondeu por último — nunca
 * decrementado localmente. Depois de uma venda o catálogo é recarregado, em
 * vez de a tela descontar as peças de cabeça.
 *
 * Dinheiro segue CONSTITUICAO.md §1: os preços chegam em centavos (inteiro), a
 * soma de tela é inteiro × inteiro e a formatação usa `plataforma/dinheiro`.
 * O total que VALE é o da resposta (INV-08) — o desta tela existe para o
 * operador dizer o valor em voz alta antes de enviar.
 *
 * Autorização: a tela esconde e desabilita por conveniência de UX; quem decide
 * é `porteiro.Guardar` (CONSTITUICAO.md §4.1). Desconto acima do limite do
 * papel volta 409 `requires_pin`, e é o servidor — não esta tela — que sabe
 * qual é o limite.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { DialogoPIN } from '../../componentes/DialogoPIN'
import { Botao, BotaoIcone } from '../../componentes/Botao'
import { usarFocoPreso } from '../../componentes/foco'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { carregarClientes, type LinhaCliente } from '../../plataforma/clientes'
import { useSessao, type Sessao } from '../../plataforma/contexto-sessao'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { mascararDocumento } from '../../plataforma/mascaras'
import {
  carregarCatalogo,
  carregarFicha,
  type Catalogo,
  type Categoria,
  type Ficha,
  type LinhaProduto,
} from '../../plataforma/produtos'
import { registrarVenda, type Venda } from '../../plataforma/vendas'
import { Fechamento, type DadosFechamento } from './Fechamento'
import { VendaConcluida } from './VendaConcluida'

/** Um item do carrinho — uma linha por SKU (produto × cor × tamanho). */
type ItemCarrinho = {
  chave: string
  codigo: string
  /**
   * O código de barras do SKU — a identidade que atravessa a fronteira em
   * `POST /vendas`. O `codigo` acima é do PRODUTO (`CAM-PERF`) e não serve
   * para vender: o que sai do estoque é a variação.
   */
  codigoBarras: string
  nome: string
  cor: string
  tamanho: string
  precoCentavos: number
  quantidade: number
  /** Saldo real do SKU no instante em que o item foi adicionado. */
  saldoMaximo: number
}

/** O erro da última tentativa de finalizar, já separado por ponto de decisão. */
type ErroDaVenda = {
  campos: Record<string, string>
  mensagem: string
}

export function TelaPDV() {
  const { situacao, pode } = useSessao()
  const sessao = situacao.estado === 'autenticado' ? situacao.sessao : null
  const podeVender = pode('sale.create')

  /* ---------------------------------------------------------- catálogo */
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [categoria, setCategoria] = useState<Categoria | ''>('')
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [erroCatalogo, setErroCatalogo] = useState('')
  // Sobe a cada venda concluída: o saldo do catálogo mudou no servidor, e
  // recarregar é mais honesto que descontar as peças na tela.
  const [versaoCatalogo, setVersaoCatalogo] = useState(0)

  // Debounce: cada tecla disparando uma consulta ao catálogo derruba o banco
  // no meio do movimento do balcão — o mesmo raciocínio de Clientes e Estoque.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()
    carregarCatalogo({ busca: buscaAtiva, categoria }, controle.signal)
      .then((dados) => {
        setCatalogo(dados)
        setErroCatalogo('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroCatalogo(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o catálogo.',
        )
      })
    return () => controle.abort()
  }, [buscaAtiva, categoria, versaoCatalogo])

  /* ------------------------------------------------------------ carrinho */
  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([])

  const definirQuantidade = useCallback((chave: string, quantidade: number) => {
    setCarrinho((atual) => {
      if (quantidade <= 0) return atual.filter((item) => item.chave !== chave)
      return atual.map((item) =>
        item.chave === chave
          ? { ...item, quantidade: Math.min(quantidade, item.saldoMaximo) }
          : item,
      )
    })
  }, [])

  const removerItem = useCallback((chave: string) => {
    setCarrinho((atual) => atual.filter((item) => item.chave !== chave))
  }, [])

  const totalItens = carrinho.reduce((soma, item) => soma + item.quantidade, 0)
  // Inteiro × inteiro: preço em centavos vezes quantidade em unidades. Nenhuma
  // divisão, nenhum ponto flutuante — CONSTITUICAO.md §1.
  const totalCentavos = carrinho.reduce(
    (soma, item) => soma + item.precoCentavos * item.quantidade,
    0,
  )

  /* --------------------------------------------------- seleção de variação */
  const [escolhendo, setEscolhendo] = useState<LinhaProduto | null>(null)
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [erroFicha, setErroFicha] = useState('')
  const [corEscolhida, setCorEscolhida] = useState('')
  const [tamanhoEscolhido, setTamanhoEscolhido] = useState('')
  const [quantidadeEscolhida, setQuantidadeEscolhida] = useState(1)

  const corAtual = ficha?.cores.find((c) => c.cor === corEscolhida)
  const indiceTamanhoAtual = ficha ? ficha.tamanhos.indexOf(tamanhoEscolhido) : -1
  const celulaAtual =
    corAtual && indiceTamanhoAtual >= 0 ? corAtual.celulas[indiceTamanhoAtual] : undefined

  const abrirEscolha = useCallback(async (produto: LinhaProduto) => {
    setEscolhendo(produto)
    setFicha(null)
    setErroFicha('')
    setCorEscolhida('')
    setTamanhoEscolhido('')
    setQuantidadeEscolhida(1)
    try {
      // A ficha traz o saldo REAL por cor × tamanho — é o que falta no card
      // do catálogo, que só tem o total agregado do produto.
      const dados = await carregarFicha(produto.codigo)
      setFicha(dados)
      setCorEscolhida(dados.cores[0]?.cor ?? '')
      setTamanhoEscolhido(dados.tamanhos.length === 1 ? (dados.tamanhos[0] ?? '') : '')
    } catch (causa: unknown) {
      setErroFicha(
        causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a ficha do produto.',
      )
    }
  }, [])

  const fecharEscolha = useCallback(() => {
    setEscolhendo(null)
    setFicha(null)
    setErroFicha('')
  }, [])

  // Esc fecha o seletor, como o drawer de Clientes e o modal do protótipo.
  useEffect(() => {
    if (!escolhendo) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fecharEscolha()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [escolhendo, fecharEscolha])

  const trocarCorEscolha = useCallback((novaCor: string) => {
    setCorEscolhida(novaCor)
    setTamanhoEscolhido('')
    setQuantidadeEscolhida(1)
  }, [])

  const trocarTamanhoEscolha = useCallback((novoTamanho: string) => {
    setTamanhoEscolhido(novoTamanho)
    setQuantidadeEscolhida(1)
  }, [])

  const incrementarQuantidade = useCallback(() => {
    setQuantidadeEscolhida((q) => (celulaAtual ? Math.min(celulaAtual.saldo, q + 1) : q))
  }, [celulaAtual])

  const decrementarQuantidade = useCallback(() => {
    setQuantidadeEscolhida((q) => Math.max(1, q - 1))
  }, [])

  const adicionarAoCarrinho = useCallback(() => {
    // Sem código de barras não há o que vender: é ele que identifica o SKU no
    // corpo de POST /vendas. Um item assim no carrinho viraria 422 no
    // fechamento, com a fila já formada.
    if (
      !ficha ||
      !corAtual ||
      !celulaAtual ||
      !celulaAtual.existe ||
      celulaAtual.saldo <= 0 ||
      !celulaAtual.codigo_barras
    ) {
      return
    }
    const quantidade = Math.max(1, Math.min(quantidadeEscolhida, celulaAtual.saldo))
    const chave = `${ficha.codigo}|${corAtual.cor}|${tamanhoEscolhido}`

    setCarrinho((atual) => {
      const jaExiste = atual.some((item) => item.chave === chave)
      if (jaExiste) {
        return atual.map((item) =>
          item.chave === chave
            ? { ...item, quantidade: Math.min(item.quantidade + quantidade, celulaAtual.saldo) }
            : item,
        )
      }
      return [
        ...atual,
        {
          chave,
          codigo: ficha.codigo,
          codigoBarras: celulaAtual.codigo_barras ?? '',
          nome: ficha.nome,
          cor: corAtual.cor,
          tamanho: tamanhoEscolhido,
          precoCentavos: celulaAtual.preco_centavos,
          quantidade,
          saldoMaximo: celulaAtual.saldo,
        },
      ]
    })
    fecharEscolha()
  }, [ficha, corAtual, celulaAtual, quantidadeEscolhida, tamanhoEscolhido, fecharEscolha])

  /* ---------------------------------------------------------------- cliente */
  const [clienteBusca, setClienteBusca] = useState('')
  const [clienteBuscaAtiva, setClienteBuscaAtiva] = useState('')
  const [clientesEncontrados, setClientesEncontrados] = useState<LinhaCliente[]>([])
  const [clienteSelecionado, setClienteSelecionado] = useState<LinhaCliente | null>(null)

  useEffect(() => {
    const relogio = setTimeout(() => setClienteBuscaAtiva(clienteBusca), 300)
    return () => clearTimeout(relogio)
  }, [clienteBusca])

  useEffect(() => {
    if (!clienteBuscaAtiva) {
      setClientesEncontrados([])
      return
    }
    const controle = new AbortController()
    carregarClientes(clienteBuscaAtiva, 'todos', controle.signal)
      .then((dados) => setClientesEncontrados(dados.linhas))
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setClientesEncontrados([])
      })
    return () => controle.abort()
  }, [clienteBuscaAtiva])

  const selecionarCliente = useCallback((cliente: LinhaCliente) => {
    setClienteSelecionado(cliente)
    setClienteBusca('')
    setClienteBuscaAtiva('')
    setClientesEncontrados([])
  }, [])

  /* ------------------------------------------------------------ finalizar */
  const [enviando, setEnviando] = useState(false)
  const [venda, setVenda] = useState<Venda | null>(null)
  const [erroVenda, setErroVenda] = useState<ErroDaVenda | null>(null)
  // A chave da permissão que o servidor pediu no 409 — hoje o caso conhecido é
  // `sale.discount_above_limit`, mas a tela não presume qual: quem nomeia é o
  // porteiro, em `campos.requires_pin`.
  const [pinPara, setPinPara] = useState('')
  const [erroPIN, setErroPIN] = useState('')
  // Guarda o que a pessoa montou para o reenvio com PIN sair IGUAL. Refazer o
  // corpo a partir do estado atual arriscaria enviar outra coisa se algo mudou
  // entre o 409 e os quatro dígitos.
  const ultimoFechamento = useRef<DadosFechamento | null>(null)

  // Mexeu no carrinho, o erro da tentativa anterior deixa de valer: ele fala de
  // um item, de uma quantidade e de um total que não são mais estes. Erro velho
  // colado numa linha nova manda corrigir o que já foi corrigido.
  useEffect(() => {
    setErroVenda(null)
  }, [carrinho])

  const finalizar = useCallback(
    async (dados: DadosFechamento, pin?: string) => {
      ultimoFechamento.current = dados
      setEnviando(true)
      setErroVenda(null)

      try {
        const criada = await registrarVenda(
          {
            cliente: clienteSelecionado?.codigo,
            itens: carrinho.map((item) => ({
              sku: item.codigoBarras,
              quantidade: item.quantidade,
              // O preço vai explícito: é o que o cliente ouviu no balcão. Se
              // divergir da tabela, o servidor trata como desconto no item e
              // aplica o mesmo limite do desconto da venda — em vez de a peça
              // sair por um preço que ninguém combinou.
              preco_unitario_centavos: item.precoCentavos,
            })),
            desconto_centavos: dados.descontoCentavos || undefined,
            pagamentos: dados.pagamentos,
          },
          pin,
        )

        setVenda(criada)
        setPinPara('')
        setErroPIN('')
      } catch (causa: unknown) {
        if (!(causa instanceof ErroApi)) {
          setErroVenda({ campos: {}, mensagem: 'Não foi possível finalizar a venda.' })
          return
        }
        // 409: a ação é permitida para este papel, mas exige a liberação de um
        // terceiro. O corpo do pedido não muda — só ganha o PIN no cabeçalho.
        if (causa.pinNecessario) {
          setPinPara(causa.pinNecessario)
          setErroPIN('')
          return
        }
        if (causa.codigo === 'pin_invalido') {
          setErroPIN(causa.message)
          return
        }
        setPinPara('')
        setErroVenda({ campos: causa.campos, mensagem: causa.message })
      } finally {
        setEnviando(false)
      }
    },
    [carrinho, clienteSelecionado],
  )

  const iniciarNovaVenda = useCallback(() => {
    setVenda(null)
    setCarrinho([])
    setClienteSelecionado(null)
    setErroVenda(null)
    setBusca('')
    setBuscaAtiva('')
    setVersaoCatalogo((atual) => atual + 1)
  }, [])

  /*
    O erro do servidor vai para o PONTO DA DECISÃO, e não para um alerta
    genérico: `item_N` marca a linha do carrinho (saldo insuficiente),
    `pagamentos`/`pagamento_N`/`cliente` marcam o bloco de pagamento
    (crediário recusado, soma que não fecha) e `desconto_centavos` marca o
    campo de desconto. A mensagem exibida é a LONGA — ela nomeia o SKU, quanto
    há em estoque e quanto falta; o texto curto de `campos` é só o endereço.
  */
  const chaveDoItemComErro = Object.keys(erroVenda?.campos ?? {}).find((chave) =>
    /^item_\d+$/.test(chave),
  )
  const indiceItemComErro = chaveDoItemComErro
    ? Number(chaveDoItemComErro.slice('item_'.length)) - 1
    : -1
  const camposDoErro = erroVenda?.campos ?? {}
  const erroDesconto = camposDoErro.desconto_centavos ? (erroVenda?.mensagem ?? '') : ''
  const erroPagamento =
    camposDoErro.pagamentos || camposDoErro.pagamento_1 || camposDoErro.pagamento_2 ||
    camposDoErro.cliente
      ? (erroVenda?.mensagem ?? '')
      : ''
  const erroGeral =
    erroVenda && indiceItemComErro < 0 && !erroDesconto && !erroPagamento ? erroVenda.mensagem : ''

  /* -------------------------------------------------------------- render */
  const chipsCategoria: { chave: Categoria | ''; rotulo: string; total?: number }[] = [
    { chave: '', rotulo: 'Todas' },
    ...(catalogo?.categorias.map((c) => ({ chave: c.chave, rotulo: c.rotulo, total: c.produtos })) ??
      []),
  ]

  // A venda concluída toma a tela inteira: o carrinho acabou, e deixar o
  // catálogo clicável ao lado convida a bipar a próxima peça numa venda que já
  // saiu. Trocar a tela também desmonta o `Fechamento`, o que zera desconto,
  // forma e valor recebido sem nenhum `reset` espalhado.
  if (venda) {
    return (
      <div className="flex h-screen flex-col bg-fundo">
        <Cabecalho sessao={sessao} />
        <VendaConcluida venda={venda} aoNovaVenda={iniciarNovaVenda} />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-fundo">
      <Cabecalho sessao={sessao} />

      <div className="flex min-h-0 flex-1">
        <section className="flex min-w-0 flex-1 flex-col gap-3.5 p-5">
          <label className="composto h-[46px] cursor-text">
            <Icone nome="barcode_scanner" tamanho={20} className="text-texto-fraco" />
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Bipe o código de barras ou digite o nome do produto"
              autoFocus
            />
            <span className="sr-only">Buscar produto</span>
          </label>

          <div className="flex flex-wrap gap-2">
            {chipsCategoria.map((chip) => {
              const ativo = chip.chave === categoria
              return (
                <button
                  key={chip.chave || 'todas'}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setCategoria(chip.chave)}
                  className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-semibold ${
                    ativo
                      ? 'border-marca bg-marca-suave text-marca-forte'
                      : 'border-borda bg-superficie text-tinta'
                  }`}
                >
                  {chip.rotulo}
                  {chip.total !== undefined && (
                    <span
                      className={`tabular rounded-full px-[6px] py-[1px] text-[11px] font-bold ${
                        ativo ? 'bg-marca-barra text-marca-forte' : 'bg-divisoria text-texto-suave'
                      }`}
                    >
                      {chip.total}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {erroCatalogo && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
              {erroCatalogo}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {!catalogo ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-[176px] animate-pulse rounded-sistema bg-superficie" />
                ))}
              </div>
            ) : catalogo.produtos.length === 0 ? (
              <div className="flex flex-col items-center gap-2.5 px-4 py-16 text-center">
                <Icone nome="search" tamanho={26} className="text-inativo" />
                <p className="text-[13.5px] font-semibold text-tinta">Nada encontrado</p>
                <p className="max-w-[320px] text-[12.5px] text-texto-suave">
                  Confira o código ou busque pelo nome.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
                {catalogo.produtos.map((produto) => (
                  <CartaoProduto
                    key={produto.codigo}
                    produto={produto}
                    aoEscolher={() => void abrirEscolha(produto)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="flex w-[400px] shrink-0 flex-col border-l border-borda bg-superficie">
          <BlocoCliente
            clienteSelecionado={clienteSelecionado}
            busca={clienteBusca}
            aoBuscar={setClienteBusca}
            resultados={clientesEncontrados}
            aoSelecionar={selecionarCliente}
            aoRemover={() => setClienteSelecionado(null)}
          />

          {erroGeral && (
            <div
              role="alert"
              className="mx-4 mt-3 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-erro-texto"
            >
              {erroGeral}
            </div>
          )}

          <ListaCarrinho
            itens={carrinho}
            aoMudarQuantidade={definirQuantidade}
            aoRemover={removerItem}
            indiceComErro={indiceItemComErro}
            mensagemDoErro={erroVenda?.mensagem ?? ''}
          />

          <Fechamento
            totalItens={totalItens}
            subtotalCentavos={totalCentavos}
            cliente={clienteSelecionado}
            podeVender={podeVender}
            enviando={enviando}
            erroDesconto={erroDesconto}
            erroPagamento={erroPagamento}
            aoFinalizar={(dados) => void finalizar(dados)}
            aoAlterar={() => setErroVenda(null)}
          />
        </aside>
      </div>

      {escolhendo && (
        <ModalEscolha
          produto={escolhendo}
          ficha={ficha}
          erro={erroFicha}
          cor={corEscolhida}
          tamanho={tamanhoEscolhido}
          quantidade={quantidadeEscolhida}
          aoTrocarCor={trocarCorEscolha}
          aoTrocarTamanho={trocarTamanhoEscolha}
          aoIncrementar={incrementarQuantidade}
          aoDecrementar={decrementarQuantidade}
          aoFechar={fecharEscolha}
          aoAdicionar={adicionarAoCarrinho}
        />
      )}

      {pinPara && (
        <DialogoPIN
          permissao={pinPara}
          erro={erroPIN}
          ocupado={enviando}
          aoConfirmar={(pin) => {
            const pendente = ultimoFechamento.current
            if (pendente) void finalizar(pendente, pin)
          }}
          aoCancelar={() => {
            setPinPara('')
            setErroPIN('')
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ peças */

function Cabecalho({ sessao }: { sessao: Sessao | null }) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-borda bg-superficie px-5">
      <Icone nome="point_of_sale" tamanho={22} className="text-marca" />
      <div className="min-w-0">
        <h1 className="font-titulo text-[16px] font-bold text-tinta">PDV</h1>
        <p className="truncate text-[12px] text-texto-suave">{sessao?.loja.nome ?? ''}</p>
      </div>
      <div className="flex-1" />
      <div className="text-right">
        <div className="text-[13px] font-semibold text-tinta">{sessao?.usuario.nome ?? ''}</div>
        <div className="text-[11.5px] text-texto-suave">{sessao?.usuario.papel_rotulo ?? ''}</div>
      </div>
    </header>
  )
}

function CartaoProduto({
  produto,
  aoEscolher,
}: {
  produto: LinhaProduto
  aoEscolher: () => void
}) {
  const semEstoque = produto.pecas === 0

  return (
    <button
      type="button"
      onClick={aoEscolher}
      // Rótulo próprio: sem ele, o nome acessível do card se confunde com o
      // dos botões de quantidade do carrinho quando o produto já foi
      // adicionado — os dois trazem "Camiseta Performance" no texto.
      aria-label={`Ver variações de ${produto.nome}`}
      className="flex flex-col overflow-hidden rounded-sistema border border-borda bg-superficie text-left"
    >
      <div className="relative flex h-[104px] items-center justify-center bg-divisoria text-inativo">
        <Icone nome="image" tamanho={26} />
        <span
          className={`tabular absolute top-2 right-2 rounded-full px-2 py-[3px] text-[11px] font-bold text-white ${
            semEstoque ? 'bg-erro' : 'bg-[rgba(31,41,55,0.82)]'
          }`}
        >
          {semEstoque ? 'sem estoque' : `${produto.pecas} un`}
        </span>
      </div>
      <div className="p-3">
        <div className="min-h-[34px] text-[13px] font-semibold text-tinta">{produto.nome}</div>
        <div className="mt-1.5 flex items-center justify-between">
          <span className="tabular text-[15px] font-bold text-tinta">
            {formatarMoeda(produto.preco_centavos)}
          </span>
          <span className="flex">
            {produto.cores.slice(0, 3).map((cor, i) => (
              <span
                key={cor.nome}
                aria-hidden
                style={{ background: cor.hex ?? '#D1D5DB', marginLeft: i ? -4 : 0 }}
                className="h-3.5 w-3.5 rounded-full border-2 border-superficie"
              />
            ))}
          </span>
        </div>
      </div>
    </button>
  )
}

function BlocoCliente({
  clienteSelecionado,
  busca,
  aoBuscar,
  resultados,
  aoSelecionar,
  aoRemover,
}: {
  clienteSelecionado: LinhaCliente | null
  busca: string
  aoBuscar: (texto: string) => void
  resultados: LinhaCliente[]
  aoSelecionar: (cliente: LinhaCliente) => void
  aoRemover: () => void
}) {
  if (clienteSelecionado) {
    return (
      <div className="flex items-center gap-2.5 border-b border-borda p-3.5">
        <Icone nome="person" tamanho={20} className="text-marca" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-semibold text-tinta">
            {clienteSelecionado.nome}
          </div>
          <div className="tabular text-xs text-texto-suave">
            {clienteSelecionado.documento
              ? mascararDocumento(clienteSelecionado.tipo, clienteSelecionado.documento)
              : clienteSelecionado.tipo_rotulo}
          </div>
        </div>
        <button
          type="button"
          onClick={aoRemover}
          className="h-[34px] shrink-0 rounded-sistema border border-borda px-3 text-[12.5px] font-semibold text-texto"
        >
          Trocar
        </button>
      </div>
    )
  }

  return (
    <div className="relative border-b border-borda p-3.5">
      <label className="composto h-[38px] cursor-text">
        <Icone nome="person_search" tamanho={18} className="text-texto-fraco" />
        <input
          value={busca}
          onChange={(evento) => aoBuscar(evento.target.value)}
          placeholder="Buscar cliente por nome, CPF ou telefone"
        />
        <span className="sr-only">Buscar cliente</span>
      </label>

      {busca && (
        <div className="absolute top-[58px] right-3.5 left-3.5 z-10 max-h-[220px] overflow-y-auto rounded-sistema border border-borda bg-superficie shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
          {resultados.length === 0 ? (
            <p className="px-3.5 py-3 text-[12.5px] text-texto-suave">Nenhum cliente encontrado.</p>
          ) : (
            resultados.map((cliente) => (
              <button
                key={cliente.codigo}
                type="button"
                onClick={() => aoSelecionar(cliente)}
                className="flex w-full flex-col items-start gap-0.5 border-t border-divisoria px-3.5 py-2.5 text-left first:border-t-0 hover:bg-fundo"
              >
                <span className="text-[13px] font-semibold text-tinta">{cliente.nome}</span>
                <span className="tabular text-xs text-texto-suave">
                  {cliente.documento
                    ? mascararDocumento(cliente.tipo, cliente.documento)
                    : cliente.tipo_rotulo}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function ListaCarrinho({
  itens,
  aoMudarQuantidade,
  aoRemover,
  indiceComErro,
  mensagemDoErro,
}: {
  itens: ItemCarrinho[]
  aoMudarQuantidade: (chave: string, quantidade: number) => void
  aoRemover: (chave: string) => void
  /** Posição que o servidor apontou em `campos.item_N`; −1 quando não houve. */
  indiceComErro: number
  mensagemDoErro: string
}) {
  if (itens.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2.5 px-6 py-10 text-center">
        <Icone nome="shopping_cart" tamanho={24} className="text-inativo" />
        <p className="text-[13.5px] font-semibold text-tinta">Nenhum item na venda</p>
        <p className="text-[12.5px] text-texto-suave">
          Toque em um produto no catálogo para adicionar.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {itens.map((item, indice) => (
        <div
          key={item.chave}
          className={`border-b border-divisoria px-4 py-3 ${
            indice === indiceComErro ? 'bg-erro-suave/40' : ''
          }`}
        >
          <div className="flex items-start gap-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-tinta">{item.nome}</div>
              <div className="tabular mt-0.5 text-xs text-texto-suave">
                {item.cor} · {item.tamanho} · {formatarMoeda(item.precoCentavos)}
              </div>
            </div>
            <span className="tabular font-bold whitespace-nowrap text-tinta">
              {formatarMoeda(item.precoCentavos * item.quantidade)}
            </span>
            <button
              type="button"
              aria-label={`Remover ${item.nome} da venda`}
              onClick={() => aoRemover(item.chave)}
              className="text-inativo"
            >
              <Icone nome="close" tamanho={17} />
            </button>
          </div>

          <div className="mt-2.5 inline-flex items-center overflow-hidden rounded-sistema border border-borda">
            <button
              type="button"
              aria-label={`Diminuir quantidade de ${item.nome}`}
              onClick={() => aoMudarQuantidade(item.chave, item.quantidade - 1)}
              className="flex h-8 w-8 items-center justify-center text-texto"
            >
              −
            </button>
            <span className="tabular flex h-8 w-10 items-center justify-center border-x border-borda text-[13px] font-bold">
              {item.quantidade}
            </span>
            <button
              type="button"
              aria-label={`Aumentar quantidade de ${item.nome}`}
              disabled={item.quantidade >= item.saldoMaximo}
              onClick={() => aoMudarQuantidade(item.chave, item.quantidade + 1)}
              className="flex h-8 w-8 items-center justify-center text-texto disabled:text-inativo"
            >
              +
            </button>
          </div>
          {item.quantidade >= item.saldoMaximo && (
            <p className="mt-1.5 text-[11.5px] text-texto-fraco">Limite do estoque disponível.</p>
          )}

          {/*
            Saldo insuficiente aparece NA LINHA que o servidor recusou, com a
            mensagem que ele escreveu — ela diz quanto a venda pediu e quanto
            há em estoque. Num alerta no alto da tela, quem tem trinta itens no
            carrinho não sabe qual deles corrigir.
          */}
          {indice === indiceComErro && mensagemDoErro && (
            <p
              role="alert"
              className="mt-2 flex items-start gap-1.5 text-[12px] leading-[1.5] font-medium text-erro"
            >
              <Icone nome="error" tamanho={15} />
              {mensagemDoErro}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

function ModalEscolha({
  produto,
  ficha,
  erro,
  cor,
  tamanho,
  quantidade,
  aoTrocarCor,
  aoTrocarTamanho,
  aoIncrementar,
  aoDecrementar,
  aoFechar,
  aoAdicionar,
}: {
  produto: LinhaProduto
  ficha: Ficha | null
  erro: string
  cor: string
  tamanho: string
  quantidade: number
  aoTrocarCor: (cor: string) => void
  aoTrocarTamanho: (tamanho: string) => void
  aoIncrementar: () => void
  aoDecrementar: () => void
  aoFechar: () => void
  aoAdicionar: () => void
}) {
  const painel = useRef<HTMLDivElement>(null)

  const corObjeto = ficha?.cores.find((c) => c.cor === cor)
  const indiceTamanho = ficha ? ficha.tamanhos.indexOf(tamanho) : -1
  const celula = corObjeto && indiceTamanho >= 0 ? corObjeto.celulas[indiceTamanho] : undefined
  const semCodigoBarras = !!celula && celula.existe && !celula.codigo_barras
  const podeAdicionar =
    !!celula && celula.existe && celula.saldo > 0 && quantidade > 0 && !semCodigoBarras

  // Este modal não usa `Dialogo` porque o cabeçalho dele é outro — miniatura,
  // nome e preço, em vez de ícone em tint. Mas o foco preso é o mesmo
  // requisito: `aria-modal` sem ele deixaria o Tab passeando pelo catálogo
  // atrás, no meio de um atendimento de balcão.
  usarFocoPreso(painel)

  return (
    <>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={aoFechar}
        className="fixed inset-0 z-40 cursor-default bg-[rgba(15,23,42,0.45)]"
      />
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={`Escolher variação de ${produto.nome}`}
        tabIndex={-1}
        className="fixed top-1/2 left-1/2 z-50 w-[520px] max-w-[94vw] -translate-x-1/2 -translate-y-1/2 rounded-card bg-superficie shadow-[0_24px_64px_rgba(15,23,42,0.28)]"
      >
        <div className="flex items-center gap-3.5 border-b border-borda p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sistema bg-divisoria text-inativo">
            <Icone nome="image" tamanho={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-titulo text-[16px] font-semibold text-tinta">{produto.nome}</div>
            <div className="tabular mt-0.5 text-[12.5px] text-texto-suave">
              {produto.codigo} · {formatarMoeda(produto.preco_centavos)}
            </div>
          </div>
          <BotaoIcone icone="close" rotulo="Fechar" tom="suave" onClick={aoFechar} />
        </div>

        <div className="p-5">
          {erro && (
            <div role="alert" className="mb-4 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
              {erro}
            </div>
          )}

          {!ficha && !erro && (
            <div className="flex flex-col gap-3">
              <div className="h-8 w-2/3 animate-pulse rounded-sistema bg-divisoria" />
              <div className="h-8 w-1/2 animate-pulse rounded-sistema bg-divisoria" />
            </div>
          )}

          {ficha && (
            <>
              <p className="mb-2.5 text-[12.5px] font-semibold text-texto">Cor</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {ficha.cores.map((c) => {
                  const ativo = c.cor === cor
                  return (
                    <button
                      key={c.cor}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => aoTrocarCor(c.cor)}
                      className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13px] font-semibold ${
                        ativo
                          ? 'border-marca bg-marca-suave text-marca-forte'
                          : 'border-borda bg-superficie text-tinta'
                      }`}
                    >
                      {c.hex && (
                        <span
                          aria-hidden
                          className="h-3.5 w-3.5 rounded-full border border-[rgba(0,0,0,0.12)]"
                          style={{ background: c.hex }}
                        />
                      )}
                      {c.cor}
                    </button>
                  )
                })}
              </div>

              <p className="mb-2.5 text-[12.5px] font-semibold text-texto">Tamanho</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {ficha.tamanhos.map((t, i) => {
                  const celulaDoTamanho = corObjeto?.celulas[i]
                  const indisponivel =
                    !celulaDoTamanho || !celulaDoTamanho.existe || celulaDoTamanho.saldo === 0
                  const ativo = t === tamanho
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={indisponivel}
                      aria-pressed={ativo}
                      aria-label={`Tamanho ${t}`}
                      onClick={() => aoTrocarTamanho(t)}
                      className={`min-w-[58px] rounded-sistema border px-3 py-2 text-center ${
                        ativo
                          ? 'border-marca bg-marca-suave'
                          : indisponivel
                            ? 'cursor-not-allowed border-borda bg-superficie-suave'
                            : 'border-borda bg-superficie'
                      }`}
                    >
                      <div
                        className={`tabular text-[13.5px] font-bold ${
                          indisponivel ? 'text-inativo' : ativo ? 'text-marca-forte' : 'text-tinta'
                        }`}
                      >
                        {t}
                      </div>
                      <div
                        className={`tabular text-[10.5px] ${
                          !celulaDoTamanho || !celulaDoTamanho.existe
                            ? 'text-inativo'
                            : celulaDoTamanho.saldo === 0
                              ? 'text-erro'
                              : 'text-texto-suave'
                        }`}
                      >
                        {!celulaDoTamanho || !celulaDoTamanho.existe
                          ? '—'
                          : celulaDoTamanho.saldo === 0
                            ? 'zerado'
                            : `${celulaDoTamanho.saldo} un`}
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <p className="mb-2 text-[12.5px] font-semibold text-texto">Quantidade</p>
                  <div className="inline-flex items-center overflow-hidden rounded-sistema border border-borda">
                    <button
                      type="button"
                      aria-label="Diminuir quantidade"
                      disabled={!celula}
                      onClick={aoDecrementar}
                      className="flex h-9 w-9 items-center justify-center text-texto disabled:text-inativo"
                    >
                      −
                    </button>
                    <span className="tabular flex h-9 w-11 items-center justify-center border-x border-borda text-[14px] font-bold">
                      {quantidade}
                    </span>
                    <button
                      type="button"
                      aria-label="Aumentar quantidade"
                      disabled={!celula || quantidade >= celula.saldo}
                      onClick={aoIncrementar}
                      className="flex h-9 w-9 items-center justify-center text-texto disabled:text-inativo"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="flex-1" />
                <div className="text-right">
                  <p className="text-[12px] text-texto-suave">Subtotal do item</p>
                  <p className="tabular text-[22px] font-bold text-tinta">
                    {formatarMoeda(celula ? celula.preco_centavos * quantidade : 0)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2.5 border-t border-borda bg-superficie-suave p-4">
          <Botao variante="secundario" porte="destaque" onClick={aoFechar}>
            Cancelar
          </Botao>
          <div className="flex-1" />
          <Botao
            porte="destaque"
            icone="shopping_cart"
            disabled={!podeAdicionar}
            onClick={aoAdicionar}
          >
            {!tamanho
              ? 'Escolha o tamanho'
              : podeAdicionar
                ? 'Adicionar à venda'
                : semCodigoBarras
                  ? 'Sem código de barras'
                  : 'Sem estoque'}
          </Botao>
        </div>
      </div>
    </>
  )
}
